import {
  Activity,
  CalendarDays,
  Edit3,
  GraduationCap,
  HeartPulse,
  Home,
  LockKeyhole,
  Mail,
  MapPin,
  Phone,
  ShieldAlert,
  UserRound,
  UsersRound,
} from "lucide-react";
import { useMemo } from "react";
import { formatDate, formatTime, statusLabel } from "../lib/utils";
import { useApp } from "../state/AppContext";
import type { Guardian, Student } from "../types";
import {
  Avatar,
  Button,
  InlineNotice,
  Modal,
  StateBadge,
  StatusBadge,
} from "./ui";
import { QrCard } from "./QrCard";

interface StudentDetailProps {
  open: boolean;
  student: Student | null;
  onClose: () => void;
  onEdit?: (student: Student) => void;
}

const calculateAge = (birthDate: string) => {
  const birth = new Date(`${birthDate}T00:00:00`);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDifference = today.getMonth() - birth.getMonth();
  if (
    monthDifference < 0 ||
    (monthDifference === 0 && today.getDate() < birth.getDate())
  ) {
    age -= 1;
  }
  return age;
};

function DetailField({
  label,
  value,
  icon: Icon,
}: {
  label: string;
  value: string;
  icon?: typeof UserRound;
}) {
  return (
    <div className="student-detail__field">
      {Icon && <Icon size={17} aria-hidden="true" />}
      <span>
        <small>{label}</small>
        <strong>{value || "Belum direkod"}</strong>
      </span>
    </div>
  );
}

function GuardianCard({
  guardian,
  canViewPhone,
}: {
  guardian: Guardian;
  canViewPhone: boolean;
}) {
  const hasDetails = guardian.name || guardian.phone || guardian.email || guardian.occupation;
  if (!hasDetails) {
    return <p className="student-detail__empty-line">Belum ada penjaga kedua direkodkan.</p>;
  }
  return (
    <article className="guardian-card">
      <header>
        <span className="guardian-card__icon">
          <UserRound size={18} />
        </span>
        <span>
          <strong>{guardian.name || "Nama belum direkod"}</strong>
          <small>{guardian.relation}</small>
        </span>
      </header>
      {canViewPhone ? (
        <>
          {guardian.phone && (
            <a href={`tel:${guardian.phone.replace(/[^\d+]/g, "")}`}>
              <Phone size={15} />
              {guardian.phone}
            </a>
          )}
          {guardian.email && (
            <a href={`mailto:${guardian.email}`}>
              <Mail size={15} />
              {guardian.email}
            </a>
          )}
        </>
      ) : (
        <span className="guardian-card__private">
          <LockKeyhole size={15} />
          Butiran hubungan disembunyikan
        </span>
      )}
      {guardian.occupation && <p>{guardian.occupation}</p>}
    </article>
  );
}

export function StudentDetail({
  open,
  student,
  onClose,
  onEdit,
}: StudentDetailProps) {
  const { session, settings, attendance } = useApp();
  const isAdmin = session?.role === "admin";
  const hasAccess =
    Boolean(student) &&
    (isAdmin ||
      settings.permissions.teacherCanViewOtherClasses ||
      student?.classId === session?.assignedClassId);
  const canViewPhone = isAdmin || settings.permissions.teacherCanViewGuardianPhone;
  const canViewMedical = isAdmin || settings.permissions.teacherCanViewMedical;
  const canDownloadQr = isAdmin || settings.permissions.teacherCanDownloadQr;

  const records = useMemo(
    () =>
      student
        ? attendance
            .filter((record) => record.studentId === student.id)
            .sort(
              (left, right) =>
                new Date(right.timestamp).getTime() - new Date(left.timestamp).getTime(),
            )
        : [],
    [attendance, student],
  );

  const attendanceCounts = useMemo(
    () =>
      records.reduce(
        (counts, record) => {
          counts[record.status] += 1;
          return counts;
        },
        { present: 0, late: 0, absent: 0, excused: 0 },
      ),
    [records],
  );

  const safeStudent = hasAccess ? student : null;

  return (
    <Modal
      open={open && Boolean(student)}
      onClose={onClose}
      title={safeStudent?.name ?? "Akses profil dihadkan"}
      description={
        safeStudent ? `${safeStudent.code} · ${safeStudent.className}` : "Profil tidak tersedia."
      }
      size="drawer"
    >
      {!safeStudent ? (
        <InlineNotice tone="danger" title="Di luar skop kelas anda">
          Pentadbir belum memberikan akses kepada profil kelas lain.
        </InlineNotice>
      ) : (
        <div className="student-detail">
          <section className="student-detail__hero">
            <div className="student-detail__hero-main">
              <Avatar
                initials={safeStudent.initials}
                size="xl"
                tone={Number(safeStudent.id.slice(-1)) || 0}
                label={`Avatar ${safeStudent.name}`}
              />
              <div>
                <span className="student-detail__class">
                  <GraduationCap size={15} />
                  {safeStudent.className}
                </span>
                <h3>{safeStudent.name}</h3>
                <div className="student-detail__hero-meta">
                  <StateBadge state={safeStudent.active ? "active" : "inactive"} />
                  <span>{safeStudent.attendanceRate}% kehadiran</span>
                </div>
              </div>
            </div>
            {isAdmin && onEdit && (
              <Button
                variant="secondary"
                size="compact"
                icon={Edit3}
                type="button"
                onClick={() => onEdit(safeStudent)}
              >
                Kemaskini
              </Button>
            )}
          </section>

          <section className="student-detail__section">
            <header className="student-detail__section-title">
              <UserRound size={18} />
              <div>
                <h3>Biodata murid</h3>
                <p>Maklumat pengenalan asas</p>
              </div>
            </header>
            <div className="student-detail__grid">
              <DetailField label="Jantina" value={safeStudent.gender} />
              <DetailField
                label="Tarikh lahir"
                value={`${formatDate(safeStudent.birthDate)} · ${calculateAge(
                  safeStudent.birthDate,
                )} tahun`}
                icon={CalendarDays}
              />
              <DetailField label="No. MyKid" value={safeStudent.myKidMasked} />
              <DetailField label="Warganegara" value={safeStudent.nationality} />
              <DetailField label="Agama" value={safeStudent.religion} />
              <DetailField
                label="Mula sekolah"
                value={safeStudent.joinedAt ? formatDate(safeStudent.joinedAt) : "—"}
                icon={GraduationCap}
              />
            </div>
            <div className="student-detail__address">
              <MapPin size={17} />
              <span>
                <small>Alamat rumah</small>
                <strong>{safeStudent.address}</strong>
              </span>
            </div>
          </section>

          <section className="student-detail__section">
            <header className="student-detail__section-title">
              <UsersRound size={18} />
              <div>
                <h3>Penjaga & kecemasan</h3>
                <p>Butiran waris yang dibenarkan</p>
              </div>
            </header>
            <div className="student-detail__guardians">
              <GuardianCard guardian={safeStudent.guardian} canViewPhone={canViewPhone} />
              <GuardianCard
                guardian={safeStudent.secondaryGuardian}
                canViewPhone={canViewPhone}
              />
            </div>
            <div className="student-detail__emergency">
              <ShieldAlert size={17} />
              <span>
                <small>Hubungan kecemasan</small>
                {canViewPhone ? (
                  safeStudent.emergencyContact ? (
                    <a
                      href={`tel:${safeStudent.emergencyContact.replace(/[^\d+]/g, "")}`}
                    >
                      {safeStudent.emergencyContact}
                    </a>
                  ) : (
                    <strong>Belum direkod</strong>
                  )
                ) : (
                  <strong className="student-detail__locked">
                    <LockKeyhole size={14} /> Disembunyikan
                  </strong>
                )}
              </span>
            </div>
          </section>

          <section className="student-detail__section">
            <header className="student-detail__section-title">
              <HeartPulse size={18} />
              <div>
                <h3>Maklumat kesihatan</h3>
                <p>Rujukan kecemasan warga sekolah</p>
              </div>
            </header>
            {canViewMedical ? (
              <div className="student-medical">
                <div className="student-medical__blood">
                  <span>Kumpulan darah</span>
                  <strong>{safeStudent.medical.bloodType || "—"}</strong>
                </div>
                <dl>
                  <div>
                    <dt>Alahan</dt>
                    <dd>{safeStudent.medical.allergies || "Tiada direkod"}</dd>
                  </div>
                  <div>
                    <dt>Keadaan kesihatan</dt>
                    <dd>{safeStudent.medical.conditions || "Tiada direkod"}</dd>
                  </div>
                  <div>
                    <dt>Arahan / catatan</dt>
                    <dd>{safeStudent.medical.notes || "Tiada arahan khas."}</dd>
                  </div>
                </dl>
              </div>
            ) : (
              <InlineNotice tone="neutral" title="Maklumat kesihatan dilindungi">
                Akses guru kepada bahagian ini dinyahaktifkan oleh pentadbir.
              </InlineNotice>
            )}
          </section>

          <section className="student-detail__section">
            <header className="student-detail__section-title">
              <Activity size={18} />
              <div>
                <h3>Rekod kehadiran</h3>
                <p>{records.length} rekod terkini dalam demo</p>
              </div>
            </header>
            <div className="attendance-mini-summary" aria-label="Ringkasan kehadiran murid">
              {(["present", "late", "absent", "excused"] as const).map((status) => (
                <span key={status}>
                  <strong>{attendanceCounts[status]}</strong>
                  <small>{statusLabel[status]}</small>
                </span>
              ))}
            </div>
            {records.length ? (
              <ol className="student-attendance-list">
                {records.slice(0, 8).map((record) => (
                  <li key={record.id}>
                    <span className="student-attendance-list__date">
                      <CalendarDays size={16} />
                      <span>
                        <strong>{formatDate(record.date)}</strong>
                        <small>
                          {formatTime(record.timestamp)} ·{" "}
                          {record.method === "qr"
                            ? "Imbas QR"
                            : record.method === "manual"
                              ? "Manual"
                              : "Pentadbir"}
                        </small>
                      </span>
                    </span>
                    <StatusBadge status={record.status} compact />
                    {record.note && <p>{record.note}</p>}
                  </li>
                ))}
              </ol>
            ) : (
              <div className="student-detail__empty-line">
                Belum ada rekod kehadiran untuk murid ini.
              </div>
            )}
          </section>

          <section className="student-detail__section student-detail__section--qr">
            <header className="student-detail__section-title">
              <Home size={18} />
              <div>
                <h3>Kad QR murid</h3>
                <p>Kod unik untuk rekod kehadiran</p>
              </div>
            </header>
            <QrCard student={safeStudent} canDownload={canDownloadQr} />
          </section>
        </div>
      )}
    </Modal>
  );
}

